import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as authApi from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import type { User } from '@/types/auth';

export function useLogin() {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: (data) => {
      // Enrich user with role/permissions from top-level arrays
      const user: User = {
        ...data.user,
        role: (data.roles?.[0] ?? 'viewer') as User['role'],
        roles: data.roles ?? [],
        permissions: data.permissions ?? [],
        first_name: data.user.first_name || data.user.name?.split(' ')[0] || '',
        last_name: data.user.last_name || data.user.name?.split(' ').slice(1).join(' ') || '',
      };
      authStore.login(user, data.token);
      queryClient.setQueryData<User>(['auth', 'me'], user);
    },
  });
}

export function useLogout() {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      authStore.logout();
      queryClient.clear();
    },
  });
}

export function useCurrentUser() {
  const { isAuthenticated } = useAuthStore();
  const authStore = useAuthStore();

  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const user = await authApi.getMe();
      authStore.setUser(user);
      return user;
    },
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000,
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
  });
}
